import type { DayOfWeek } from "@/entities/shift/model/types";

export const ShiftType = {
  Open: "Open",
  Standard: "Standard",
} as const;

export type ShiftType = (typeof ShiftType)[keyof typeof ShiftType];

export const ShiftTypeLabels: Record<ShiftType, string> = {
  [ShiftType.Open]: "Open",
  [ShiftType.Standard]: "Standard",
};

/**
 * A guard a saved template still names whose employment has ended, so the template plans
 * coverage they can no longer be given.
 *
 * Templates are deliberately never auto-cleaned server-side — the guard list is a plan the
 * office authored and deactivation is reversible — so this is the signal that the office should
 * remove them deliberately. A pending hire who has never been activated is not listed here;
 * they are still rosterable.
 */
export interface ShiftTemplateFormerEmployee {
  guardUserId: number;
  guardName: string;
}

export interface ShiftTemplate {
  id: number;
  accountId: number;
  title: string;
  startTime: string;
  endTime: string;
  siteId: number | null;
  categoryId: number | null;
  department: string | null;
  skillSet: string | null;
  note: string | null;
  isActive: boolean;
  recurrenceDays: DayOfWeek[] | null;
  recurrenceWeeks: number | null;
  guardUserIds: number[];
  /**
   * Which of `guardUserIds` no longer have account access. Carried by the list and by-id GETs;
   * the create and update responses do not report it, hence optional — read it as `?? []`.
   */
  guardsNoLongerEmployed?: ShiftTemplateFormerEmployee[];
}

export interface CreateShiftTemplateRequest {
  title: string;
  startTime: string;
  endTime: string;
  siteId?: number | null;
  categoryId?: number | null;
  department?: string | null;
  skillSet?: string | null;
  note?: string | null;
  recurrenceDays?: DayOfWeek[] | null;
  recurrenceWeeks?: number | null;
  guardUserIds?: number[];
}

export interface UpdateShiftTemplateRequest {
  title?: string;
  startTime?: string;
  endTime?: string;
  siteId?: number | null;
  categoryId?: number | null;
  department?: string | null;
  skillSet?: string | null;
  note?: string | null;
  recurrenceDays?: DayOfWeek[] | null;
  recurrenceWeeks?: number | null;
  guardUserIds?: number[];
}
